import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import './lazy-image.css';

type LoadState = 'loading' | 'loaded' | 'error';

export interface LazyImageProps
    extends Omit<
        React.ImgHTMLAttributes<HTMLImageElement>,
        'width' | 'height' | 'loading'
    > {
    src: string;
    /** Required: decorative images pass "". */
    alt: string;
    /** Intrinsic size of the delivered image; reserves its aspect ratio. */
    width: number;
    height: number;
    /**
     * Above-the-fold / LCP image: loads eagerly with high fetch priority and
     * shows no skeleton. Everything else lazy-loads behind a skeleton.
     */
    priority?: boolean;
}

/**
 * The site's image element. Renders a bare <img> (no wrapper, so callers'
 * classes and layout keep working) with explicit width/height, native lazy
 * loading and a shimmer skeleton until the image loads.
 *
 * The real <img> is always in the server HTML and is never hidden, so images
 * still show without JavaScript and crawlers see them.
 */
const LazyImage = forwardRef<HTMLImageElement, LazyImageProps>(
    (
        {
            src,
            alt,
            width,
            height,
            priority = false,
            onLoad,
            onError,
            decoding,
            ...rest
        },
        forwardedRef
    ) => {
        const [state, setState] = useState<LoadState>(
            priority ? 'loaded' : 'loading'
        );
        const imgRef = useRef<HTMLImageElement | null>(null);

        const setRefs = useCallback(
            (node: HTMLImageElement | null) => {
                imgRef.current = node;
                if (typeof forwardedRef === 'function') forwardedRef(node);
                else if (forwardedRef) forwardedRef.current = node;
            },
            [forwardedRef]
        );

        // A cached image can finish before React attaches onLoad during
        // hydration, so the event never fires; read the element instead.
        // Re-run when src changes (carousels, variant pickers).
        useEffect(() => {
            if (priority) return;
            const img = imgRef.current;
            if (img?.complete) {
                setState(img.naturalWidth > 0 ? 'loaded' : 'error');
            } else {
                setState('loading');
            }
        }, [src, priority]);

        const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
            setState('loaded');
            onLoad?.(event);
        };

        const handleError = (event: React.SyntheticEvent<HTMLImageElement>) => {
            setState('error');
            onError?.(event);
        };

        return (
            <img
                ref={setRefs}
                src={src}
                alt={alt}
                width={width}
                height={height}
                loading={priority ? 'eager' : 'lazy'}
                decoding={decoding ?? (priority ? 'auto' : 'async')}
                // React 18 warns on camelCase fetchPriority; pass it lowercase.
                {...({ fetchpriority: priority ? 'high' : 'auto' } as any)}
                data-lazy-image={state}
                onLoad={handleLoad}
                onError={handleError}
                {...rest}
            />
        );
    }
);

LazyImage.displayName = 'LazyImage';

export default LazyImage;
